import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || 'tongren-manhua');

  useEffect(() => {
    if (slug) setActiveCat(slug);
  }, [slug]);

  useEffect(() => {
    document.title = `${getCatName()} - 雏田同人馆`;
  }, [activeCat]);

  function getCatName() {
    return categories.find((c) => c.slug === activeCat)?.name || '分类';
  }

  const cat = categories.find((c) => c.slug === activeCat);
  const filteredArticles = articles.filter((a) => a.category === activeCat);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${getCatName()} - 雏田同人馆`,
    description: cat?.description || '',
    url: `https://hinata-doujin.readdy.ai/category/${activeCat}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: getCatName() },
            ]}
          />

          <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  {getCatName()}
                </h1>
                {cat && (
                  <p className="text-sm text-foreground-500">
                    {cat.description} · 共 {cat.count} 部作品
                  </p>
                )}
              </div>

              <div className="flex gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeCat === c.slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${c.icon} text-xs`}></i>
                    </span>
                    {c.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
                    data-product-shop=""
                  >
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-2 flex-wrap">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-foreground-500">暂无该分类的作品，请浏览其他分类</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}